import React, { useEffect, useState } from "react";
import {
  Form,
  Row,
  Input,
  Col,
  Card,
  Table,
  Grid,
  Spin,
  Button,
  Space,
  type TableProps,
} from "antd";

import { ClearOutlined, SearchOutlined } from "@ant-design/icons";

import { maskCnpj, maskNumeroProcesso, numberOnly } from "../utils/maskFunctions";

import { errorMessage, infoMessage } from "../messages/messages";
import type { IPessoaJuridica } from "./Interfaces";

import api from "../services/api";
import { isValidNup } from "../utils/utilsFunctions";

const { useBreakpoint } = Grid;

interface IProps {
  textoBotao: string;
  setPessoaJuridica: React.Dispatch<React.SetStateAction<IPessoaJuridica | undefined>>;
}

const SelecionarPessoaJuridica = ({ textoBotao, setPessoaJuridica }: IProps) => {
  const [form] = Form.useForm();

  const screens = useBreakpoint();

  const [pessoasJuridicas, setPessoasJuridicas] = useState<IPessoaJuridica[]>([]);

  const [totalElements, setTotalElements] = useState(0);
  const [linhaPorPagina, setLinhaPorPagina] = useState(10);
  const [pagina, setPagina] = useState(1);

  const [loading, setLoading] = useState(false);
  const [tipLoading, setTipLoading] = useState("");

  const [pessoaJuridicaSelecionada, setPessoaJuridicaSelecionada] = useState<IPessoaJuridica>();
  const [pessoaJuridicaSelectedRowKeys, setPessoaJuridicaSelectedRowKeys] = useState<React.Key[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0)
  }, []);

  useEffect(() => {
    if (pessoaJuridicaSelecionada) {
      setPessoaJuridica(pessoaJuridicaSelecionada);
    }
  }, [pessoaJuridicaSelecionada]);


  function gerarColunas() {
    let cols: any[] = [];

    cols.push({
      // key: "cnpj",
      width: 150,
      title: "CNPJ",
      dataIndex: "",
      render: (pj: IPessoaJuridica) => (
        <Input
          value={pj.tipoIdentificador === 1 ? maskCnpj(pj.numero) : undefined}
          size={"small"}
          style={{ padding: 0 }}
          variant="borderless"
        />
      ),
    });

    cols.push({
      // key: "tipoIdentificador",
      width: 150,
      title: "Número de Identificação Fiscal",
      dataIndex: "",
      render: (pj: IPessoaJuridica) => (
        <Input
          value={pj.tipoIdentificador === 2 ? pj.numero : undefined}
          size={"small"}
          style={{ padding: 0 }}
          variant="borderless"
        />
      ),
    });

    cols.push(
      screens.xs
        ? null
        :
        {
          // key: "numeroProcesso",
          width: 150,
          title: "Número Processo",
          dataIndex: "",
          render: (pj: IPessoaJuridica) => (
            <Input
              value={pj.numeroProcesso ? maskNumeroProcesso(pj.numeroProcesso) : undefined}
              size={"small"}
              style={{ padding: 0 }}
              variant="borderless"
            />
          ),
        }

    );

    cols.push({
      // key: "razaoSocial",
      // width: 400,
      title: "Nome Empressarial",
      dataIndex: "razaoSocial",
      width: screens.xs ? 400 : screens.xl ? 300 : 200,
    });

    return cols.filter(Boolean);
  }

  const columns = gerarColunas();

  const validarConsulta = () => {
    const currentForm = form.getFieldsValue();
    if ((currentForm?.cnpjNumero && currentForm?.cnpjNumero !== "") && (currentForm?.numeroIdentificacaoFiscal && currentForm?.numeroIdentificacaoFiscal !== "")) {
      errorMessage("Os campos CNPJ e Número de Identificação Fiscal foram preenchidos. A consulta não pode ser feita com ambos os campos preenchidos.");
      return
    }

    if (currentForm.numeroProcesso && !isValidNup(currentForm.numeroProcesso)) {
      errorMessage("Número do Processo inválido.");
      return
    }
    getPessoasJuridicas();

  };

  const getPessoasJuridicas = async () => {
    try {
      setLoading(true);
      setTipLoading("Pesquisando ...");

      const currentForm = form.getFieldsValue();

      const numero = currentForm?.cnpjNumero && currentForm?.cnpjNumero !== "" ? numberOnly(currentForm?.cnpjNumero) : (currentForm?.numeroIdentificacaoFiscal ? numberOnly(currentForm?.numeroIdentificacaoFiscal) : "")
      const razaoSocial = currentForm.razaoSocial || "";
      const numeroProcesso = numberOnly(currentForm.numeroProcesso) || "";

      let lQuery = `consulta/dinamica?numero=${numero}&razao-social=${razaoSocial}&numero-processo=${numeroProcesso}&page-size=${linhaPorPagina}&page=0`;

      const { data } = await api.get(lQuery);
      setLoading(false);

      setPessoasJuridicas(data.content);
      setTotalElements(data.totalElements);

      if (data.content.length === 0) {
        infoMessage("O sistema não encontrou resultado para consulta realizada");
      }
    } catch (error: any) {
      errorMessage("Não foi possível realizar a consulta. " + error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePaginationChange = async (page: number, pageSize: number) => {
    setLoading(true);

    setPagina(page);
    setLinhaPorPagina(pageSize);

    const numero = numberOnly(form.getFieldValue("numero")) || "";
    const razaoSocial = form.getFieldValue("razaoSocial") || "";
    const numeroProcesso = numberOnly(form.getFieldValue("numeroProcesso")) || "";

    let lQuery = `consulta/dinamica?numero=${numero}&razao-social=${razaoSocial}&numero-processo=${numeroProcesso}&page-size=${pageSize}&page=${page - 1}`;

    const { data } = await api.get(lQuery);

    setPessoasJuridicas(data.content);
    setLoading(false);
  };

  function limparConsulta() {
    setPessoasJuridicas([]);
    setPessoaJuridicaSelecionada(undefined);
    setTotalElements(0);
    form.resetFields();
  }

  const pessoaJuridicaRowSelection: TableProps<IPessoaJuridica>['rowSelection'] = {
    type: "radio", // ensures only one row selected
    selectedRowKeys: pessoaJuridicaSelectedRowKeys,
    onChange: (newSelectedKeys, selectedRows: IPessoaJuridica[]) => {
      setPessoaJuridicaSelectedRowKeys(newSelectedKeys);
      setPessoaJuridicaSelecionada(selectedRows[0]); // store selected object
    },
  };


  return (
    <Spin spinning={loading} tip={tipLoading}>
      <Form layout="vertical" form={form}>

        <Card>
          <Row>
            <Col xs={24} xl={7}>
              <Form.Item
                label="CNPJ"
                name='cnpjNumero'
              >
                <Input maxLength={18}
                  onChange={(e) =>
                    form.setFieldsValue({
                      cnpjNumero: maskCnpj(e.target.value),
                    })
                  }
                />
              </Form.Item>
            </Col>

            <Col xs={24} xl={7}>
              <Form.Item
                label="Número de Identificação Fiscal (NIF)"
                name='numeroIdentificacaoFiscal'
              >
                <Input
                  maxLength={21}
                />
              </Form.Item>
            </Col>

            <Col xs={24} xl={7}>
              <Form.Item
                label="Número do Processo"
                name='numeroProcesso'
              >
                <Input
                  maxLength={21}
                  onChange={(e) =>
                    form.setFieldsValue({
                      numeroProcesso: maskNumeroProcesso(e.target.value),
                    })
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col xs={24} xl={16}>
              <Form.Item label="Nome Empresarial"
                name='razaoSocial'
              >
                <Input
                  id="razaoSocial"
                  placeholder="Digitar o nome" />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="end">
            <Space align='center' >
              {
                <>
                  <Button shape='round' type='primary' icon={<SearchOutlined />} htmlType='button'
                    onClick={() => {
                      validarConsulta();
                    }} >
                    {textoBotao}
                  </Button>
                  <Button disabled={false} shape='round' icon={<ClearOutlined />} htmlType='button'
                    onClick={() => {
                      limparConsulta();
                      window.scrollTo(0, 0)
                    }} >
                    Limpar
                  </Button>
                </>
              }
            </Space>
          </Row>
        </Card>

        <Card title={"Resultado da consulta - " + totalElements + " encontrados"} style={{ display: totalElements === 0 ? 'none' : 'inline' }}>
          <Table
            rowSelection={pessoaJuridicaRowSelection}
            dataSource={pessoasJuridicas}
            size="middle"
            columns={columns}
            rowKey="serie"
            loading={loading}
            pagination={{
              current: pagina,
              pageSize: linhaPorPagina,
              total: totalElements,
              showSizeChanger: true,
              onChange: handlePaginationChange,
              pageSizeOptions: ["10", "20", "25", "30"],
              locale: { items_per_page: "Linhas por página" },
            }}
            scroll={{ x: "max-content", y: 350 }}
          />
        </Card>
      </Form>

    </Spin >
  );
};

export default SelecionarPessoaJuridica;
